import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Login from './components/Login';
import MFAVerification from './components/MFAVerification';
import MainLayout from './components/Layout/MainLayout';
import Dashboard from './components/Dashboard';
import UserManagement from './components/UserManagement';
import LicenseManagement from './components/LicenseManagement';
import EmailLog from './components/EmailLog';
import UserDetail from './components/UserDetail';
import LicenseDetail from './components/LicenseDetail';
import { getAuthToken, setAuthToken, clearAuthToken } from './services/auth';
import './App.css';

const ADMIN_THEME_STORAGE_KEY = 'zeitece_admin_theme'; // shared with legacy admin

function App() {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = getAuthToken();
    setIsAuthenticated(!!token);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let mq = null;
    let mqListener = null;

    const getPref = () => {
      const v = (localStorage.getItem(ADMIN_THEME_STORAGE_KEY) || '').trim();
      return (v === 'light' || v === 'dark' || v === 'system') ? v : 'system';
    };

    const resolve = (pref) => {
      if (pref === 'light' || pref === 'dark') return pref;
      try {
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      } catch (_e) {
        return 'light';
      }
    };

    const apply = (pref) => {
      const resolved = resolve(pref);
      document.body.classList.toggle('dark-mode', resolved === 'dark');
      document.body.setAttribute('data-theme', resolved);
      document.body.setAttribute('data-theme-pref', pref);
    };

    const wireSystemListener = (pref) => {
      if (mq && mqListener) {
        try {
          mq.removeEventListener('change', mqListener);
        } catch (_e) {
          try { mq.removeListener(mqListener); } catch (_e2) { /* ignore */ }
        }
      }
      mq = null;
      mqListener = null;

      if (pref !== 'system') return;
      if (!window.matchMedia) return;

      mq = window.matchMedia('(prefers-color-scheme: dark)');
      mqListener = () => apply('system');
      try {
        mq.addEventListener('change', mqListener);
      } catch (_e) {
        try { mq.addListener(mqListener); } catch (_e2) { /* ignore */ }
      }
    };

    const pref = getPref();
    apply(pref);
    wireSystemListener(pref);

    const onStorage = (e) => {
      if (!e || e.key !== ADMIN_THEME_STORAGE_KEY) return;
      const p = getPref();
      apply(p);
      wireSystemListener(p);
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      if (mq && mqListener) {
        try {
          mq.removeEventListener('change', mqListener);
        } catch (_e) {
          try { mq.removeListener(mqListener); } catch (_e2) { /* ignore */ }
        }
      }
    };
  }, []);

  const handleLogin = (token) => {
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return <div className="admin-loading">{t('admin.loading_app')}</div>;
  }

  return (
    <Router>
      <Routes>
        <Route path="/admin/login" element={
          !isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/admin/dashboard" />
        } />
        <Route path="/admin/mfa" element={
          !isAuthenticated ? <MFAVerification onLogin={handleLogin} /> : <Navigate to="/admin/dashboard" />
        } />
        <Route path="/admin/*" element={
          isAuthenticated ? (
            <MainLayout onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="users/:id" element={<UserDetail />} />
                <Route path="licenses" element={<LicenseManagement />} />
                <Route path="licenses/:key" element={<LicenseDetail />} />
                <Route path="email-log" element={<EmailLog />} />
                <Route path="*" element={<Navigate to="/admin/dashboard" />} />
              </Routes>
            </MainLayout>
          ) : <Navigate to="/admin/login" />
        } />
        <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
      </Routes>
    </Router>
  );
}

export default App;






